import { Fragment, memo, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Bubble,
  BubbleContent,
  ChatChartBlock,
  ChatDocumentBlock,
  ChatEntityViewBlock,
  ChatFeedbackBlock,
  ChatMarkdown,
  InlineEntityCard,
  Message,
  MessageAvatar,
  MessageContent,
  MessageSources,
  type ChatEntityCardRenderer,
  type ChatFeedbackHandler
} from '@bliks-io/ui';
import { env } from '@/config/env';
import { ChatAttachmentBlock } from './ChatAttachment';
import { chatRendererData, downloadDocument } from './chatRendererAdapters';
import {
  isChartPart,
  isDocumentPart,
  isEntityViewPart,
  isFilePart,
  isTextPart,
  textFromParts,
  type TranscriptChartPart,
  type TranscriptDocumentPart,
  type TranscriptEntityViewPart,
  type TranscriptFilePart,
  type TranscriptPart
} from './parts';
import type { AiProvenance, ChatEntityRef, ChatMessageUserFeedback } from '@bliks-io/contracts';

/** The sources row's fixed React key — a turn carries at most one. */
const SOURCES_BLOCK_KEY = 'sources';

/** The thumbs row's fixed React key — a turn carries at most one. */
const FEEDBACK_BLOCK_KEY = 'feedback';

/** What the thumbs row needs to submit a vote on this turn. */
interface FeedbackBlockPayload {
  messageId: string;
  userFeedback?: ChatMessageUserFeedback;
  onFeedback: ChatFeedbackHandler;
}

/**
 * One rendered block of a turn: markdown, a chart, a download card, an embedded
 * entity view, an attachment, the sources row or the thumbs row.
 */
type MessageBlock =
  | { kind: 'text'; key: string; text: string }
  | { kind: 'chart'; key: string; part: TranscriptChartPart }
  | { kind: 'document'; key: string; part: TranscriptDocumentPart }
  | { kind: 'entityView'; key: string; part: TranscriptEntityViewPart }
  | { kind: 'attachment'; key: string; part: TranscriptFilePart }
  | { kind: 'sources'; key: string; refs: ChatEntityRef[]; slug: string }
  | ({ kind: 'feedback'; key: string } & FeedbackBlockPayload);

/**
 * The ordered walk over a turn's parts. Two rules carry the weight:
 *
 * - **Adjacent text parts merge into one block.** Streaming splits a turn
 *   mid-sentence, and markdown that straddles a split (a bold run, a table row)
 *   only renders when the pieces are parsed as one document.
 * - **Unknown part types produce nothing.** A backend newer than this bundle may
 *   send a part type it has never heard of; it must be inert, never fatal.
 */
function toBlocks(parts: TranscriptPart[] | undefined): MessageBlock[] {
  const blocks: MessageBlock[] = [];
  parts?.forEach((part, index) => {
    if (isTextPart(part)) {
      const previous = blocks[blocks.length - 1];
      if (previous?.kind === 'text') {
        previous.text += part.text;
      } else {
        blocks.push({ kind: 'text', key: `text-${index}`, text: part.text });
      }
      return;
    }
    if (isChartPart(part)) {
      blocks.push({ kind: 'chart', key: part.toolCallId ?? `chart-${index}`, part });
      return;
    }
    if (isDocumentPart(part)) {
      blocks.push({ kind: 'document', key: part.toolCallId ?? `document-${index}`, part });
      return;
    }
    if (isEntityViewPart(part)) {
      blocks.push({ kind: 'entityView', key: part.toolCallId ?? `entity-view-${index}`, part });
      return;
    }
    if (isFilePart(part)) {
      blocks.push({ kind: 'attachment', key: part.storageKey ?? `file-${index}`, part });
    }
  });
  return blocks;
}

/**
 * The turn-level rows are blocks like any other, so the render loop needs no
 * index arithmetic to place them. Each describes the whole turn, so there is at
 * most one of each: the sources row trails the LAST text block (the chips dedupe
 * against the `entity://` links anywhere in the joined text), and a turn made
 * only of charts has no text block to trail, so it goes last. The thumbs row
 * rates the whole answer, so it always comes last — below a wide chart when the
 * turn ends in one.
 */
function toRenderedBlocks(
  parts: TranscriptPart[] | undefined,
  sources: { refs: ChatEntityRef[]; slug: string } | null,
  feedback: FeedbackBlockPayload | null
): MessageBlock[] {
  const blocks = toBlocks(parts);

  if (sources) {
    const lastTextBlockIndex = blocks.map((block) => block.kind).lastIndexOf('text');
    const position = lastTextBlockIndex === -1 ? blocks.length : lastTextBlockIndex + 1;
    blocks.splice(position, 0, { kind: 'sources', key: SOURCES_BLOCK_KEY, ...sources });
  }
  if (feedback) {
    blocks.push({ kind: 'feedback', key: FEEDBACK_BLOCK_KEY, ...feedback });
  }
  return blocks;
}

interface ChatMessageProps {
  role: 'user' | 'assistant';
  /** AI SDK v5 parts array */
  parts?: TranscriptPart[];
  /** Structured entity sources for an assistant message. */
  entityRefs?: ChatEntityRef[];
  /**
   * The EU AI-Act mark of an assistant message. Its PRESENCE — not the role —
   * puts `data-ai-generated` on the message content: deterministic assistant messages (a
   * templated greeting, an attachment handoff) and messages written before the
   * mark existed carry none and stay unmarked (plan 022 D6/D7).
   */
  aiProvenance?: AiProvenance;
  /** The turn key a vote is submitted under — the id the backend stores. */
  messageId?: string;
  /** The reader's standing verdict on this answer, if any. */
  userFeedback?: ChatMessageUserFeedback;
  /**
   * Submits a vote. Its presence is what puts the thumbs row on screen, so a
   * host that has not wired feedback renders exactly as before — WHICH turns
   * are votable is decided by `ChatMessages`, never re-derived here. MUST be
   * referentially stable: this component is memoised.
   */
  onFeedback?: ChatFeedbackHandler;
  /** Icon to show for assistant messages */
  assistantIcon: ReactNode;
  /** Avatar URL for user messages */
  userAvatar?: string;
  className?: string;
  /** Organization slug for entity links */
  slug?: string;
}

/**
 * Shared chat message row, built on the shadcn chat primitives.
 * User turns render as an accent bubble aligned to the end. Assistant turns
 * frame each text block in a `muted` bubble, while wide blocks (charts,
 * record views, download cards) break out of the bubble and take the full
 * message width — a chart in a shrink-to-fit bubble would collapse to the
 * width of whatever text sits beside it. The sources chips and the thumbs
 * row render unframed below the content they describe.
 */
const ChatMessageInner = ({
  role,
  parts,
  entityRefs,
  aiProvenance,
  messageId,
  userFeedback,
  onFeedback,
  assistantIcon,
  userAvatar,
  className,
  slug
}: ChatMessageProps) => {
  const { t } = useTranslation();

  // `ChatMarkdown` knows which references are entities; the chip itself needs
  // the API client and the router, so it is supplied from here. Stable per
  // slug — it feeds a memoised components map that must not be rebuilt on a
  // streamed token. Without a slug there is nowhere to link to, so an
  // `entity://` reference stays an ordinary link, as it did before.
  const renderEntityCard = useMemo<ChatEntityCardRenderer | undefined>(
    () =>
      slug
        ? ({ type, id, name }) => (
            <InlineEntityCard data={chatRendererData} type={type} id={id} name={name} slug={slug} />
          )
        : undefined,
    [slug]
  );
  const textContent = textFromParts(parts);

  if (role === 'user') {
    return (
      <Message align="end" className={className}>
        <MessageAvatar className="self-start">
          <Avatar className="size-full rounded-full">
            <AvatarImage src={userAvatar} alt={t('common.you')} className="object-cover" />
            <AvatarFallback className="text-xs font-medium">{t('common.you')}</AvatarFallback>
          </Avatar>
        </MessageAvatar>
        <MessageContent className="flex flex-col items-end gap-2">
          {parts?.filter(isFilePart).map((part, index) => (
            <ChatAttachmentBlock key={part.storageKey ?? `file-${index}`} part={part} />
          ))}
          {textContent && (
            <Bubble variant="accent" align="end">
              <BubbleContent>{textContent}</BubbleContent>
            </Bubble>
          )}
        </MessageContent>
      </Message>
    );
  }

  const sources =
    env.USE_SOURCES_UI && slug && entityRefs && entityRefs.length > 0
      ? { refs: entityRefs, slug }
      : null;
  const feedback = messageId && onFeedback ? { messageId, userFeedback, onFeedback } : null;
  const blocks = toRenderedBlocks(parts, sources, feedback);

  const renderBlock = (block: MessageBlock): ReactNode => {
    switch (block.kind) {
      case 'text':
        // `max-w-full` widens the bubble past the framed default: an answer's
        // markdown carries pipe tables, and capping those at 85% of the column
        // buys nothing but an earlier horizontal scrollbar. A mermaid diagram
        // (or its loading skeleton) has no intrinsic width and would collapse
        // to the width of whatever text sits beside it under `w-fit` — the
        // `has-` variants below force the bubble AND its content slot to full
        // width whenever the block contains one.
        return (
          <Bubble
            variant="muted"
            className="max-w-full has-[[data-mermaid]]:w-full has-[[data-mermaid]]:*:data-[slot=bubble-content]:w-full"
          >
            <BubbleContent>
              <div className="prose prose-sm text-foreground prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0">
                <ChatMarkdown text={block.text} renderEntityCard={renderEntityCard} />
              </div>
            </BubbleContent>
          </Bubble>
        );
      case 'chart':
        return <ChatChartBlock part={block.part} />;
      case 'document':
        return <ChatDocumentBlock part={block.part} download={downloadDocument} />;
      case 'entityView':
        return <ChatEntityViewBlock part={block.part} slug={slug} data={chatRendererData} />;
      case 'attachment':
        return <ChatAttachmentBlock part={block.part} />;
      case 'sources':
        // The FULL joined text of the turn, not this block's: the chips dedupe
        // against every entity:// link in the message, wherever it sits.
        return (
          <MessageSources
            refs={block.refs}
            text={textContent}
            renderEntityCard={({ type, id, name }) => (
              <InlineEntityCard
                data={chatRendererData}
                type={type}
                id={id}
                name={name}
                slug={block.slug}
              />
            )}
          />
        );
      case 'feedback':
        return (
          <ChatFeedbackBlock
            messageId={block.messageId}
            userFeedback={block.userFeedback}
            onFeedback={block.onFeedback}
          />
        );
      default: {
        // `ReactNode` includes `undefined`, so an eighth block kind would
        // type-check with no arm here and silently render nothing — the whole
        // turn's content, gone, with no failing test. Same guard the entity-view
        // switches carry.
        const unhandled: never = block;
        throw new Error(`unhandled message block: ${JSON.stringify(unhandled)}`);
      }
    }
  };

  return (
    <Message className={className}>
      <MessageAvatar variant="icon">{assistantIcon}</MessageAvatar>
      <MessageContent data-ai-generated={aiProvenance ? 'true' : undefined}>
        {blocks.map((block) => (
          <Fragment key={block.key}>{renderBlock(block)}</Fragment>
        ))}
      </MessageContent>
    </Message>
  );
};

export const ChatMessage = memo(ChatMessageInner);
